import { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Accordion } from 'react-bootstrap';
import Select from 'react-select';
import { Formik, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import Tippy from '@tippyjs/react';
import { Link } from 'react-router-dom';
import { AccordionToggleWithButtonForRole } from '../common';
import { SelectPermission } from '.';

const CreateRole = ({
  handleCreateRole,
  handleShowCreateRole,
  showCreateRole,
  createTypesList,
  createPermissionList,
  sitesList,
}) => {
  const [toggleArrowChange, arrowChange] = useState(false);
  const [typeList, typeListSet] = useState([]);
  const [permissionsList, permissionsListSet] = useState([]);
  const [permissionsListFinal, permissionsListFinalSet] = useState({});
  const [userTypeValue, userTypeValueChange] = useState('');

  const mangerType =
    createTypesList?.length > 0 &&
    createTypesList.find((item) => item.label === 'Manager');

  const arrowChangeToggle = () => {
    arrowChange(!toggleArrowChange);
  };

  const userTypeChange = (value) => {
    userTypeValueChange(value);
    const typeNameIndex = permissionsList.findIndex(
      (item) => item.typeId === value
    );
    if (typeNameIndex > -1) {
      permissionsListFinalSet(permissionsList[typeNameIndex]);
    }
  };

  const siteOptions = sitesList.map((site) => ({
    value: site._id,
    label: site.siteName,
  }));

  useEffect(() => {
    /* eslint-disable no-unused-expressions */
    typeListSet(createTypesList);
    permissionsListSet(createPermissionList);
  }, [createTypesList, createPermissionList]);

  return (
    <>
      <Button
        variant="primary"
        size="sm"
        onClick={() => {
          userTypeValueChange('');
          handleShowCreateRole(true);
        }}
      >
        Create New Role
      </Button>

      <Modal
        show={showCreateRole}
        onHide={() => {
          handleShowCreateRole(false);
        }}
        centered
      >
        <Modal.Header>
          <Modal.Title className="m-0">Create New Role</Modal.Title>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => {
              handleShowCreateRole(false);
            }}
          />
        </Modal.Header>
        <Formik
          enableReinitialize
          initialValues={{
            roleName: '',
            userType: '',
            selectedPermissions: [],
            siteId: '',
          }}
          validationSchema={Yup.object({
            roleName: Yup.string().required('Please enter a name'),
            userType: Yup.string().required('Please select user type'),
            selectedPermissions: Yup.array().min(
              1,
              'Please select at least one permission.'
            ),
          })}
          onSubmit={(values) => {
            handleCreateRole(values);
          }}
        >
          {(formikprops) => {
            const handleSiteChange = (selectedOptions) => {
              // If "All" is selected, select all options
              if (selectedOptions.some((option) => option.value === 'all')) {
                formikprops.setFieldValue('siteId', 'all');
              } else {
                // Otherwise, update with selected options
                formikprops.setFieldValue(
                  'siteId',
                  selectedOptions.map((item) => item.value)
                );
              }
            };
            return (
              <Form onSubmit={formikprops.handleSubmit}>
                <Modal.Body>
                  <section>
                    <Form.Group controlId="worksessionType">
                      <Form.Label>Role Name</Form.Label>
                      <Field
                        className="form-control"
                        type="text"
                        placeholder="Enter role name"
                        name="roleName"
                      />
                      <ErrorMessage
                        name="roleName"
                        render={(msg) => (
                          <small className="text-danger">{msg}</small>
                        )}
                      />
                    </Form.Group>

                    <Form.Group controlId="type">
                      <Form.Label className="w-100">
                        <div className="float-start">
                          User Type
                          <Tippy
                            content={`${'Default type to inherit permission from'}`}
                            theme="light"
                            key="2"
                          >
                            <span className="p-2 d-inline-block">
                              <i className="mdi mdi-information-outline" />
                            </span>
                          </Tippy>
                        </div>
                        <div className="float-end">
                          <ul className="nav">
                            <li className="nav-item ">
                              <Link
                                to="/help/permissions"
                                className="nav-link nav-underline text-decoration-underline"
                                target="_blank"
                                rel="noreferrer"
                              >
                                User type permissions
                              </Link>
                            </li>
                          </ul>
                        </div>
                      </Form.Label>

                      <Select
                        name="userType"
                        options={typeList}
                        onChange={(e) => {
                          formikprops.setFieldValue('userType', e.value);
                          formikprops.setFieldValue('selectedPermissions', []);
                          userTypeChange(e.value);
                        }}
                      />
                      <ErrorMessage
                        name="userType"
                        render={(msg) => (
                          <small className="text-danger">{msg}</small>
                        )}
                      />
                    </Form.Group>

                    {userTypeValue &&
                      userTypeValue === mangerType?.value &&
                      siteOptions?.length > 0 && (
                        <Form.Group controlId="siteId">
                          <Form.Label>Select Sites</Form.Label>
                          <Select
                            isMulti
                            name="siteId"
                            options={[
                              { value: 'all', label: 'All' },
                              ...siteOptions,
                            ]}
                            onChange={handleSiteChange}
                            placeholder="Select sites"
                            className="site-multi-select"
                            classNamePrefix="select"
                            theme={(theme) => ({
                              ...theme,
                              colors: {
                                ...theme.colors,
                                primary: '#0649b5',
                                neutral10: '#edf2f9',
                                neutral20: '#d2ddec',
                                neutral30: '#d2ddec',
                                neutral50: '#b1c2da',
                                neutral80: '#12263f',
                                dangerLight: '#DEEBFF',
                                danger: '#12263f',
                              },
                            })}
                          />
                          <ErrorMessage
                            name="client"
                            render={(msg) => (
                              <small className="text-danger">{msg}</small>
                            )}
                          />
                        </Form.Group>
                      )}

                    {userTypeValue && (
                      <div>
                        <h4 className="mb-3">Select Permissions</h4>
                      </div>
                    )}

                    {userTypeValue && (
                      <div className="card mb-0">
                        <div className="card-body p-0">
                          <ul className="list-group list-group-flush pe-0">
                            <Accordion
                              defaultActiveKey="0"
                              className="accordion-flush"
                            >
                              {permissionsListFinal &&
                                permissionsListFinal.permissions &&
                                permissionsListFinal.permissions.length > 0 &&
                                permissionsListFinal.permissions.map(
                                  (permission, index) => (
                                    <li className="list-group-item" key={index}>
                                      <Row>
                                        <Col className="px-2">
                                          <div className="d-flex justify-content-start text-capitalize">
                                            {permission.name}
                                          </div>
                                        </Col>
                                        <Col sm lg="1">
                                          <div className="d-flex justify-content-end">
                                            <AccordionToggleWithButtonForRole
                                              eventKey={index + 1}
                                              callback={arrowChangeToggle}
                                            />
                                          </div>
                                        </Col>
                                      </Row>

                                      <Accordion.Collapse eventKey={index + 1}>
                                        <div className="p-2">
                                          {permission.list &&
                                            permission.list.length > 0 &&
                                            permission.list.map((item, i) => (
                                              <SelectPermission
                                                permissionNameLabel={
                                                  item.permissionNameUI
                                                }
                                                key={i}
                                                permissionName={
                                                  item.permissionName
                                                }
                                                formikprops={formikprops}
                                              />
                                            ))}
                                        </div>
                                      </Accordion.Collapse>
                                    </li>
                                  )
                                )}
                            </Accordion>
                          </ul>
                        </div>
                      </div>
                    )}
                    <ErrorMessage
                      name="selectedPermissions"
                      render={(msg) => (
                        <small className="text-danger">{msg}</small>
                      )}
                    />
                  </section>
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-between">
                  <Button
                    variant="link"
                    onClick={() => {
                      handleShowCreateRole(false);
                    }}
                  >
                    Cancel
                  </Button>

                  <Button variant="primary" type="submit">
                    Create
                  </Button>
                </Modal.Footer>
              </Form>
            );
          }}
        </Formik>
      </Modal>
    </>
  );
};

CreateRole.propTypes = {
  handleCreateRole: PropTypes.func,
  handleShowCreateRole: PropTypes.func,
  showCreateRole: PropTypes.bool,
  createTypesList: PropTypes.array,
  createPermissionList: PropTypes.array,
  sitesList: PropTypes.array,
};

export default CreateRole;
